<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Pagination from 'dashboard/components/table/Pagination.vue';
import ReportDataTable from '../ReportDataTable.vue';
import ReportEmptyState from '../ReportEmptyState.vue';
import Avatar from 'next/avatar/Avatar.vue';
import { renderLiveCount, sortByOpenThenName } from './liveTableHelpers';

const props = defineProps({
  teams: {
    type: Array,
    default: () => [],
  },
  teamMetrics: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

// UI Settings key for team table page size
const TEAM_TABLE_PAGE_SIZE_KEY = 'report_overview_team_table_page_size';
const MIN_PAGE_SIZE = 10;

// Get the saved page size from UI settings or default to 10
const getPageSize = () => {
  return uiSettings.value[TEAM_TABLE_PAGE_SIZE_KEY] || MIN_PAGE_SIZE;
};

const handlePageSizeChange = pageSize => {
  updateUISettings({ [TEAM_TABLE_PAGE_SIZE_KEY]: pageSize });
};

const getTeamMetrics = id =>
  props.teamMetrics.find(metrics => metrics.team_id === Number(id)) || {};

const tableData = computed(() =>
  props.teams
    .map(team => {
      const metric = getTeamMetrics(team.id);
      return {
        agent: team.name,
        open: metric.open || 0,
        unattended: metric.unattended || 0,
      };
    })
    .sort(sortByOpenThenName)
);

const renderTeamCell = cellProps =>
  h('div', { class: 'flex min-w-0 items-center gap-3' }, [
    h(Avatar, { name: cellProps.getValue(), size: 28 }),
    h(
      'span',
      {
        class: 'truncate text-sm font-medium text-n-slate-12',
        title: cellProps.getValue(),
      },
      cellProps.getValue()
    ),
  ]);

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('agent', {
    header: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.TEAM'),
    cell: renderTeamCell,
  }),
  columnHelper.accessor('open', {
    header: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.OPEN'),
    cell: renderLiveCount,
    size: 120,
    meta: { align: 'end' },
  }),
  columnHelper.accessor('unattended', {
    header: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.UNATTENDED'),
    cell: renderLiveCount,
    size: 140,
    meta: { align: 'end' },
  }),
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: getPageSize(),
    },
  },
});

const showSkeleton = computed(() => props.isLoading && !tableData.value.length);
const showPagination = computed(() => tableData.value.length > MIN_PAGE_SIZE);
</script>

<template>
  <div class="flex min-w-0 flex-1 flex-col">
    <div
      v-if="showSkeleton"
      class="flex flex-col gap-3 px-5 pb-5 pt-2"
      role="status"
      :aria-label="$t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.LOADING_MESSAGE')"
    >
      <div
        v-for="item in 4"
        :key="item"
        class="h-10 rounded-lg bg-n-alpha-2 motion-safe:animate-pulse"
      />
    </div>
    <ReportEmptyState
      v-else-if="!teams.length"
      icon="i-hugeicons-user-group"
      :title="$t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.NO_TEAMS')"
    />
    <template v-else>
      <ReportDataTable
        :table="table"
        min-width-class="min-w-[28rem]"
        scroll-class="max-h-[30rem] overflow-y-auto"
      />
      <Pagination
        v-if="showPagination"
        class="border-t border-n-weak px-5 py-3 dark:border-n-strong"
        :table="table"
        show-page-size-selector
        :default-page-size="getPageSize()"
        @page-size-change="handlePageSizeChange"
      />
    </template>
  </div>
</template>
